/**
 * TanStack Query key roots whose payload is constituent PII and which are
 * therefore excluded from localStorage persistence by `shouldDehydrateQuery`
 * in `lib/query-persistence.ts`.
 *
 * Each root is minted by exactly one hook AND compared against by the exclusion
 * chain. Centralising the literal here binds the two structurally: a rename is a
 * single edit that moves the hook and the exclusion together, so a future rename
 * of a hook's key root cannot silently re-enable PII-at-rest persistence (the
 * two-lists-drift class that `share-gated-cache-registry.ts` documents from a
 * past incident).
 *
 * Kept dependency-free — importing it into the eagerly-loaded persistence module
 * must not pull the hooks' react-query / supabase graph into the App bundle
 * (same rationale as `use-email-index/email-index-key.ts`).
 */

export const SCHEDULED_DRAFTS_QUERY_KEY = 'scheduled-drafts';
export const RECIPIENT_CONTACTS_QUERY_KEY = 'recipient-contacts';
export const FORWARDED_EMAIL_LIST_QUERY_KEY = 'forwarded-email-list';
export const REJECTED_EMAILS_QUERY_KEY = 'rejected-emails';
/**
 * The viewer's own WebAuthn credentials rather than constituent data, but it
 * belongs to the same binding: the root is authentication state whose friendly
 * names are user-chosen ("Will's iPhone"), and a rehydrated snapshot would
 * resurrect a credential an admin has since revoked. Minted here so
 * `passkey-keys.ts`'s factory and the exclusion chain rename together.
 */
export const PASSKEYS_QUERY_KEY = 'passkeys';
/**
 * CRM import job root. The preview sub-key holds the raw uploaded cells - the
 * header row plus up to 20 data rows of constituent names, emails, phones and
 * addresses, straight off the operator's file. Excluded at the ROOT so every
 * present and future `crmImportJobKeys.*` sub-key is covered by construction;
 * the preview query in particular sets no refetch trigger at all, so a
 * rehydrated snapshot would seed the mapping UI without re-consulting the
 * admin-role gate inside the SECURITY DEFINER RPC that produced it.
 */
export const CRM_IMPORT_JOBS_QUERY_KEY = 'crm-import-jobs';
/**
 * Potential-duplicate review pages contain the visible sides' names, contact
 * details, dates of birth, and current company values. The database rechecks
 * both sides on every read, so a browser must never rehydrate an older visible
 * result after either constituent's visibility changes.
 */
export const POTENTIAL_DUPLICATES_QUERY_KEY = 'potential-duplicates';
/**
 * The on-behalf send capability read. Not constituent data — an authorization
 * answer, which is why it belongs to the same binding as `passkeys`: a
 * rehydrated `true` would keep an affordance live for up to `maxAge` after the
 * grant was withdrawn, without re-consulting `user_can_answer_on_behalf`.
 * Fail-closed means it must be re-derived on every load, never restored.
 */
export const ON_BEHALF_SEND_CAPABILITY_QUERY_KEY = 'on-behalf-send-capability';
/**
 * The connected messaging accounts list (Messenger / Instagram / WhatsApp).
 * Visibility is viewer-scoped at RLS — personal accounts are SELECTable only
 * by their owner — so the live key carries the viewer id as a suffix and the
 * root must never persist: snapshots written by builds that keyed on org
 * alone restore for whichever same-org user signs in next, and even a
 * viewer-suffixed snapshot keeps account metadata (display names, external
 * account ids) in plaintext localStorage past the session and resurrects
 * disconnected/purged accounts for up to maxAge with refetch-on-mount off
 * app-wide. Visibility must be re-decided by RLS on every load, never
 * restored. Same posture as `email-accounts`.
 */
export const MESSAGING_ACCOUNTS_QUERY_KEY = 'messaging-accounts';
/**
 * External mailbox identities include display names, email addresses and
 * viewer-scoped personal ownership. They must be re-authorized through RLS on
 * every load and never restored from plaintext localStorage after a session.
 */
export const EXTERNAL_EMAIL_MAILBOXES_QUERY_KEY = 'external-email-mailboxes';
/**
 * The external-email send read model and sender grants. The send state carries
 * an authorization answer (`can_send`, `can_unsend`), the approval hash the
 * client echoes back, and staff names; a rehydrated `can_send: true` would keep
 * Mark as sent live after a grant was revoked. Sender grants name who may send.
 * Both must be re-derived on every load, never restored.
 */
export const EXTERNAL_EMAIL_SEND_QUERY_KEY = 'external-email-send';
/**
 * The reads behind Move into conversation: the move picker lists other cases'
 * titles on a mailbox, searched by sender name and address, and the suggestion
 * names cases from the same sender. Visibility is decided per mailbox by the
 * server on every load; a restored list would outlive a personal mailbox's
 * owner change or a revoked membership.
 */
export const EXTERNAL_EMAIL_CAPTURE_QUERY_KEY = 'external-email-capture';
/**
 * The social reply-window anchor. Two reasons it must never restore from disk:
 * it is a countdown anchor whose hook runs `staleTime: 0` plus a per-minute
 * `refetchInterval` precisely so a stale anchor is never rendered, and the value
 * is the constituent's last inbound timestamp on a correspondence.
 *
 * The anchor itself is serialisable — `MessagingWindowAnchor` carries epoch
 * millis — so a leak here degrades rather than crashing. This exclusion is the
 * correctness guard, not the crash guard.
 */
export const MESSENGER_WINDOW_QUERY_KEY = 'messenger-window';
/**
 * Sub-key (second key element under the gmail/outlook roots) of the
 * unsubscribe-info query. Its payload's `senderKey` falls back to the
 * normalized sender address when the newsletter carries no List-Id, so a
 * persisted snapshot writes an email address to localStorage past the
 * session. Minted here so the hook's key factory and the
 * `isFullMessagePayloadQuery` exclusion rename together.
 */
export const UNSUBSCRIBE_INFO_QUERY_SUBKEY = 'unsubscribe-info';

export const WEBSITES_QUERY_KEY = 'websites';
/**
 * Per-viewer resolution of a draft's cited knowledge: document titles, file
 * names, storage paths and the quoted excerpts, read under the viewer's RLS
 * (`useDraftSourceEvidence`). The live key carries the actor and organisation,
 * and the root must never persist: a snapshot would hand a later sign-in on
 * the same browser another member's resolved evidence, and would keep showing
 * an original after its sharing or the viewer's membership was revoked.
 */
export const DRAFT_SOURCE_EVIDENCE_QUERY_KEY = 'draft-source-evidence';
/**
 * A sent email's draft citations (`useSentDraftCitations`). The raw entry holds the draft body
 * in memory, readable through a correspondence share, so the root must never persist and is
 * also hard-evicted on a share revoke (`share-gated-cache-registry`).
 */
export const SENT_DRAFT_CITATIONS_QUERY_KEY = 'sent-draft-citations';
/** Signed URLs and downloaded originals (`storageKeys.all`); excluded from persistence. */
export const STORAGE_QUERY_KEY = 'storage';
/** Knowledge lists, details and search (`knowledgeKeys.all`); excluded from persistence. */
export const KNOWLEDGE_QUERY_KEY = 'knowledge';
/**
 * The roots resolved under the viewer's RLS (ADR 0091): cited evidence, signed
 * original URLs and downloads, knowledge lists. Their live keys carry actor and
 * organisation. An organisation switch cancels and removes all of them
 * (`use-organization-switching`); a change to the viewer's own membership
 * refetches them (`lib/knowledge/evidence-cache-invalidation`).
 */
export const VIEWER_SCOPED_EVIDENCE_ROOTS = [
  DRAFT_SOURCE_EVIDENCE_QUERY_KEY,
  SENT_DRAFT_CITATIONS_QUERY_KEY,
  STORAGE_QUERY_KEY,
  KNOWLEDGE_QUERY_KEY,
] as const;
